import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogContent from '@mui/material/DialogContent';
import { useStringsContext } from '../../../../contexts/strings';
import DialogActions from '@mui/material/DialogActions';
import { Grid2 as Grid, Stack } from '@mui/material';
import RenderModuleField from '../../../common/fields/render-fields';
import BaseDialog, { BaseDialogProps } from '../../../common/base-dialog';
import { useArticles } from '../hooks/useArticles';
import { isAllFieldsValid } from '../../../../utils/team';
import { Article, Author, CreateArticleInput, UpdateArticleInput } from '../types';
import { getArticleSchema } from '../UI-schema';
import { getArticleStatus } from '../utils/status';
import { useAuthors } from '../hooks/useAuthors';
import moment from 'moment';
import { useAppConfigOptions } from '../../../../hooks/appConfigs';

interface Props extends Pick<BaseDialogProps, 'open' | 'onClose'> {
  onDone: () => void;
  onPreview: (id: string) => void;
  article: Article;
}

function EditArticleModal({ open, onClose, onDone, onPreview, article }: Props) {
  const { strings } = useStringsContext();
  const { createArticle, updateArticle } = useArticles();
  const { fetchAuthors } = useAuthors();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const valuesFields = useRef<Record<string, any>>({});
  const editedFields = useRef<Record<string, boolean>>({});
  const [authors, setAuthors] = useState<Author[]>([]);
  const { appConfigOptions } = useAppConfigOptions();

  useEffect(() => {
    fetchAuthors().then(setAuthors);
  }, [fetchAuthors]);

  const onUpdateArticle = useCallback(
    async (articleData: UpdateArticleInput) => {
      setSubmitLoading(true);
      await updateArticle(articleData);

      onDone();
      onClose?.();
      setSubmitLoading(false);
    },
    [onDone, onClose, updateArticle]
  );

  const onSubmit = useCallback(
    async (preview: boolean) => {
      setSubmitLoading(true);
      let id = article.id;
      const input = {
        ...(valuesFields.current as CreateArticleInput),
        publishedAt:
          valuesFields.current.publishedAt != null
            ? moment(valuesFields.current.publishedAt).utc().toDate()
            : valuesFields.current.publishedAt,
        archivedAt:
          valuesFields.current.archivedAt != null
            ? moment(valuesFields.current.archivedAt).utc().toDate()
            : valuesFields.current.archivedAt,
      };
      if (id) {
        await updateArticle({ ...input, id });
      } else {
        const newArticleId = await createArticle(input);
        if (newArticleId) {
          id = newArticleId;
        }
      }

      if (preview && id) {
        onPreview(id);
      } else {
        onDone();
      }
      setSubmitLoading(false);
    },
    [article, onDone, onPreview, createArticle, updateArticle]
  );

  const uiScheme = useMemo(() => {
    if (appConfigOptions && authors) {
      return getArticleSchema(appConfigOptions, authors, article);
    }
  }, [appConfigOptions, authors, article]);

  const onChange = (fieldKey: string, value: any) => {
    valuesFields.current[fieldKey] = value;
    setIsDirty(true);
  };

  const onValidation = async (
    fieldKey: string,
    value: string | number | boolean,
    validation: any
  ) => {
    const isCurrentFieldValid: boolean = await validation.isValid(value);
    editedFields.current[fieldKey] = isCurrentFieldValid;
    const isFormValid = isAllFieldsValid(editedFields.current);
    setSubmitDisabled(!isFormValid);
  };

  const onArchive = () => {
    onUpdateArticle({ id: article.id, archivedAt: new Date() });
  };

  const status = getArticleStatus(article);

  return (
    <BaseDialog open={open} onClose={onClose} title={strings.articles.editArticle} maxWidth="md">
      <DialogContent dividers>
        <Grid container spacing={4}>
          {uiScheme?.fields.map((field) => (
            <Grid size={12} key={field.id}>
              <RenderModuleField
                field={field}
                setChange={onChange}
                setValidation={onValidation}
                path="article"
                imagePath="articles"
                fullWidth
              />
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Stack direction="row" gap={2} sx={{ justifyContent: 'space-between', flex: 1 }}>
          <Stack>
            {status === 'published' && (
              <Button color="error" onClick={onArchive}>
                {strings.actions.archive}
              </Button>
            )}
          </Stack>
          <Stack direction="row" gap={2}>
            <Button color="primary" onClick={onClose}>
              {strings.actions.cancel}
            </Button>
            <LoadingButton
              variant="contained"
              color="primary"
              disabled={submitDisabled || !isDirty}
              loading={submitLoading}
              onClick={() => onSubmit(false)}
            >
              {strings.actions.save}
            </LoadingButton>
            <LoadingButton
              variant="contained"
              color="primary"
              disabled={submitDisabled || !isDirty}
              loading={submitLoading}
              onClick={() => onSubmit(true)}
            >
              {strings.actions.saveAndPreview}
            </LoadingButton>
            {article.id && (
              <Button variant="contained" color="primary" onClick={() => onPreview(article.id)}>
                {strings.actions.preview}
              </Button>
            )}
          </Stack>
        </Stack>
      </DialogActions>
    </BaseDialog>
  );
}

export default EditArticleModal;
